/**
 * The native markup data model. Coordinates are normalized 0..1 within the page; rendering
 * multiplies by the page's size in PDF points, so shapes, strokes and text scale with the sheet.
 */

import { viewerMarkupPalette } from "../../../theme/palettes";
import type { MarkupImageAttachment } from "./imageAttachments";

/** A point in normalized page space (0..1). */
export interface Pt {
    x: number;
    y: number;
}

/** A page's size in points (page px): what normalized coordinates are scaled by. */
export interface PageSize {
    width: number;
    height: number;
}

/** An axis-aligned box in normalized page space (0..1), matching ApiBounds. */
export interface Bounds {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
}

/** The server's payload caps: past one, every autosave 400s and the draft is stuck failing. */
export const MAX_SHAPES_PER_SET = 500;
export const MAX_POINTS_PER_SHAPE = 5000;
export const MAX_TEXT_LENGTH = 5000;

/**
 * The size (page px) a markup's words start at when nobody chose one (a Callout's, a Cloud+'s, a
 * cloud's): the server's `MARKUP_FONT_SIZE`, which `contractSnapshot.fresh.test.ts` holds this
 * copy to; iOS mirrors it as `Preset.defaultCalloutFontSizePagePoints`.
 */
export const MARKUP_FONT_SIZE = 14;

/**
 * Marvin's markups (schema 1.5.0, `ai_engine/app/markup_schema.py`). A shape Marvin drew on the user's
 * behalf carries `origin: "marvin"` and starts not kept (`kept: false`); the user keeps or removes it.
 * The viewer draws it in its stored colour (Marvin's blue unless the user asked for colour coding), dashed until
 * kept; an export carries it only once kept.
 */
export const MARVIN_ORIGIN = "marvin";
export const MARVIN_MARKUP_COLOR = viewerMarkupPalette.marvin;

/** PDF base-14-safe text font families; the only allowed `fontFamily` values. */
export type MarkupFontFamily = "Helvetica" | "Times" | "Courier";

/** Ordered list for the font picker and validation reuse; first entry is the default. */
export const MARKUP_FONT_FAMILIES: MarkupFontFamily[] = ["Helvetica", "Times", "Courier"];

/** Stroke dash (schema 1.2.0) on the outline kinds, not on cloud's bumps; absent => solid. One
 *  exception is drawn, never stored: a Marvin markup not yet kept is dashed, a cloud included
 *  (`drawnShape`). */
export type MarkupDash = "solid" | "dashed" | "dotted";

/** One end of a line/arrow's stroke: a filled arrowhead, or a bare (capless) end. */
export type MarkupEndCap = "none" | "arrow";

/** Line and arrow end caps (schema 1.2.0); an absent end is its kind's default: "arrow" only at
 *  an arrow's end. */
export interface MarkupEndCaps {
    start?: MarkupEndCap;
    end?: MarkupEndCap;
}

/** Text weight for text/callout glyphs (schema 1.2.0); absent => "normal". */
export type MarkupFontWeight = "normal" | "bold";

/** Text style for text/callout glyphs (schema 1.2.0); absent => "normal". */
export type MarkupFontStyle = "normal" | "italic";

/** Text alignment for text/callout glyphs (schema 1.2.0); absent => "left". */
export type MarkupTextAlign = "left" | "center" | "right";

/** The stamp presets (schema T1): the one source `StampPreset` and every preset map derive from;
 *  the first is the default. */
export const STAMP_PRESETS = [
    "approved",
    "rejected",
    "revised",
    "forReview",
    "superseded",
] as const;

/** Stamp preset values, derived from `STAMP_PRESETS`. */
export type StampPreset = (typeof STAMP_PRESETS)[number];

/** The preset a freshly placed stamp uses — always the tuple's first entry. */
export const DEFAULT_STAMP_PRESET: StampPreset = STAMP_PRESETS[0];

/** Uppercase rubber-stamp label per preset, rendered centered/bold on the stamp shape. */
export const STAMP_PRESET_LABELS = {
    approved: "APPROVED",
    rejected: "REJECTED",
    revised: "REVISED",
    forReview: "FOR REVIEW",
    superseded: "SUPERSEDED",
} as const satisfies Record<StampPreset, string>;

/** The cap on a custom stamp's text (schema 1.3.0): rubber-stamp copy, a few short lines. */
export const MAX_STAMP_TEXT_LENGTH = 200;

/** The placeholders a custom stamp's template may embed (schema 1.3.0), resolved at placement;
 *  `stampDynamicFields.ts` derives its field list from it. */
export const STAMP_DYNAMIC_FIELDS = ["date", "time", "user", "project"] as const;

/** Dynamic-field placeholder values, derived from `STAMP_DYNAMIC_FIELDS`. */
export type StampDynamicField = (typeof STAMP_DYNAMIC_FIELDS)[number];

/**
 * A custom stamp's text (schema 1.3.0): `template` keeps the raw placeholders (lines joined by
 * "\n") so a re-place resolves them again; `text` is what was last resolved, the only field
 * render, export and summary read.
 */
export interface StampCustomText {
    template: string;
    text: string;
}

/** Content queued for the next stamp placement (Tool Chest, the editor's "place now"), consumed
 *  by the gesture that builds it; null places the default preset. */
export type PendingStampContent =
    | { kind: "custom"; custom: StampCustomText; subject?: string }
    | { kind: "image"; imageRef: string; imageAspectRatio: number; subject?: string };

/**
 * A snapshot queued for the next Image placement ("Place on a page…"), consumed by the gesture:
 * `naturalWidth`/`naturalHeight` are the region's size in points; `imageRef` the picture this tab
 * took of it, when it took one.
 */
export interface PendingSnapshotImage {
    snapshot: MarkupSnapshot;
    imageRef: string | null;
    naturalWidth: number;
    naturalHeight: number;
}

/** What the next Image placement puts down: a picked image's sidecar, or a snapshot. */
export type PendingImage = MarkupImageAttachment | PendingSnapshotImage;

/** The text-markup styles (GAP-SPEC P1.4), the one source the style type, its labels and the
 *  iOS parity test read. */
export const TEXT_MARKUP_STYLES = ["highlight", "underline", "strikeout"] as const;

/** Text-markup style values, derived from `TEXT_MARKUP_STYLES`. */
export type TextMarkupStyle = (typeof TEXT_MARKUP_STYLES)[number];

/** List-row "Kind" label per text-markup style (GAP-SPEC P1.4 dock contract). */
export const TEXT_MARKUP_STYLE_LABELS = {
    highlight: "Text highlight",
    underline: "Text underline",
    strikeout: "Text strikeout",
} as const satisfies Record<TextMarkupStyle, string>;

/** The cap on one text markup's quads (a quad per selected line-run), far above a real clause. */
export const MAX_QUADS_PER_TEXT_MARKUP = 512;

/** The cap on a text markup's snippet: list-row and search metadata, not content. */
export const TEXT_MARKUP_SNIPPET_MAX = 200;

/** Review statuses (P0.1, Revu's Review model); "None" is an absent or null `status`. */
export const MARKUP_STATUSES = ["accepted", "rejected", "completed", "cancelled"] as const;

/** Review status values, derived from `MARKUP_STATUSES`. */
export type MarkupStatus = (typeof MARKUP_STATUSES)[number];

/** Display label per review status; "None" (absent/null) has no entry by design. */
export const MARKUP_STATUS_LABELS = {
    accepted: "Accepted",
    rejected: "Rejected",
    completed: "Completed",
    cancelled: "Cancelled",
} as const satisfies Record<MarkupStatus, string>;

/** One status palette for the web panel, the desktop dock, the canvas and iOS (MarkupApi.swift). */
export const MARKUP_STATUS_COLORS = {
    // BRAND.md semantic-token 700-stops (chips tint these; glyphs fill with them).
    accepted: viewerMarkupPalette.success,
    rejected: viewerMarkupPalette.danger,
    completed: viewerMarkupPalette.primary,
    cancelled: viewerMarkupPalette.neutral,
} as const satisfies Record<MarkupStatus, string>;

/** The canvas and native glyph per status (GAP-SPEC P0.1). */
export const MARKUP_STATUS_GLYPHS = {
    accepted: "\u2713",
    rejected: "\u2715",
    completed: "\u2713",
    cancelled: "\u25CF",
} as const satisfies Record<MarkupStatus, string>;

/** The cap on one shape's replies, which never count as shapes; each obeys `MAX_TEXT_LENGTH`. */
export const MAX_REPLIES_PER_SHAPE = 100;

/**
 * One review reply, stored on the shape (it has no geometry of its own): the store, undo,
 * clipboard and persistence treat one shape as one row. `createdAt` is required.
 */
export interface MarkupReply {
    id: string;
    /** 1..MAX_TEXT_LENGTH after trimming. */
    text: string;
    createdByName?: string | null;
    createdAt: string;
}

export interface MarkupAppearance {
    /** Hex like "#E53935". */
    color: string;
    /** 0..1 */
    opacity: number;
    /** Page px (PDF points), so line weight scales with the sheet and matches the export. */
    strokeWidth: number;
    /** Helvetica | Times | Courier, for text and callouts; absent => Helvetica. */
    fontFamily?: string;
    /**
     * The next text or callout's font size (page px), set by the pre-draw strip; builders strip
     * it elsewhere. Absent => DEFAULT_TEXT_FONT_SIZE, or `MARKUP_FONT_SIZE` for words
     * (`wordsFontSize`).
     */
    fontSize?: number;
    /** Interior fill (schema 1.1.0), persisted only on FILLABLE_SHAPE_TYPES; absent => outline. */
    fillColor?: string;
    /** On top of `opacity`; 1 when absent. */
    fillOpacity?: number;
    /** Schema 1.2.0, persisted only on the dash-eligible kinds. */
    dash?: MarkupDash;
    /** Schema 1.2.0, persisted only on line and arrow. */
    endCaps?: MarkupEndCaps;
    /** Schema 1.2.0; this and the next two persist only on text and callouts. */
    fontWeight?: MarkupFontWeight;
    fontStyle?: MarkupFontStyle;
    align?: MarkupTextAlign;
}

/** The shape types this build knows; any other is a newer client's, carried through saves
 *  verbatim (MARKUP_SCHEMA.md rule 4). */
export const KNOWN_SHAPE_TYPES: ReadonlySet<string> = new Set([
    "rectangle",
    "ellipse",
    "cloud",
    "line",
    "arrow",
    "freehand",
    "polyline",
    "polygon",
    "measureLength",
    "measureArea",
    "measureAngle",
    "measureRadius",
    "measureDiameter",
    "measureVolume",
    "count",
    "note",
    "flag",
    "image",
    "text",
    "callout",
    "cloudCallout",
    "dimension",
    "stamp",
    "textMarkup",
]);

/**
 * The shape types the server stores (`ai_engine/app/markup_schema.py`, 1.4.0), less `image`,
 * which only an attachment page stores, as a snapshot (`keepsShape`). This build is ahead of the
 * server for Stamp, Flag, Dimension and the angle, radius, diameter and volume measures, which
 * only a local document's own file keeps. One unknown `type` fails the save's validation and
 * rejects the whole page's draft with a 422, so every boundary reads this set through
 * `keepsKind`: no tool offers such a shape, a paste or a Tool Chest card refuses one, and
 * `MarkupApi.saveDraft` drops any that reach it while the page keeps it with a save error.
 * A kind joins the set in the change that lands it in the ai_engine model
 * (`.claude/rules/markup-schema.md` there); the set retires when it equals the known types.
 */
export const SERVER_SHAPE_TYPES: ReadonlySet<string> = new Set([
    "rectangle",
    "ellipse",
    "cloud",
    "line",
    "arrow",
    "freehand",
    "polyline",
    "polygon",
    "measureLength",
    "measureArea",
    "count",
    "text",
    "callout",
    "cloudCallout",
    "note",
]);

/** Kinds whose persisted form may carry `fillColor`/`fillOpacity`. */
export const FILLABLE_SHAPE_TYPES: ReadonlySet<string> = new Set([
    "rectangle",
    "ellipse",
    "cloud",
    "polygon",
    "text",
    "callout",
    "cloudCallout",
]);

/** Shape types whose stroke renders the `dash` pattern (cloud arcs stay solid). */
export const DASH_ELIGIBLE_TYPES: ReadonlySet<string> = new Set([
    "rectangle",
    "ellipse",
    "line",
    "arrow",
    "polyline",
    "polygon",
]);

/** The line/arrow kind for end-cap arrowheads, or null for every other type/tool. */
export function capKindOf(type: string): "line" | "arrow" | null {
    return type === "line" || type === "arrow" ? type : null;
}

interface MarkupBase extends MarkupAppearance {
    id: string;
    /** Optional on legacy shapes, like `createdAt`. */
    createdByName?: string | null;
    createdAt?: string | null;
    /** Review status: null and absent both mean "None" (writers delete the key); a paste or
     *  duplicate strips it. */
    status?: MarkupStatus | null;
    /** Free-form subject (Bluebeam parity), as null as `status`: it overrides the derived
     *  summary and the exported `/Subj`, and survives a paste. */
    subject?: string | null;
    /** The dock's checklist mark (absent => unchecked), never exported. */
    checked?: boolean;
    /** Oldest first; capped at MAX_REPLIES_PER_SHAPE. */
    replies?: MarkupReply[];
    /**
     * A locked shape stays selectable but rejects every move, resize, restyle, text edit and
     * delete (the store's `updateShape`, `deleteShape`, `deleteSelected` no-op on it); z-order
     * and review still change. Unlocking deletes the key.
     */
    locked?: boolean;
    /**
     * Group membership: shapes sharing the id act as one for click-select (Alt-click picks one),
     * move, nudge, delete, copy, lock and order. Opaque and internal; a paste remaps it to a
     * fresh id, so the clones form their own group.
     */
    groupId?: string;
    /**
     * Who drew the shape when it was not the user (schema 1.5.0): `"marvin"` for a markup the assistant made
     * on the user's behalf. Absent => the user's own. Carried through every edit and save unchanged.
     */
    origin?: typeof MARVIN_ORIGIN;
    /**
     * Whether the user kept a Marvin markup (schema 1.5.0). Only meaningful with `origin`: not kept (absent
     * or false) it is dashed and stays out of every export; kept it is solid and exported.
     */
    kept?: boolean;
    /** The conversation and message that made a Marvin markup (schema 1.5.0). */
    originConversationId?: string;
    originMessageId?: string;
}

/**
 * A markup, discriminated by `type`. Highlighter persists as a `freehand` with its preset baked
 * in. `rotation` (bounds kinds) is degrees clockwise about the bounds center; line and arrow
 * rotate by their endpoints. `callout` is a text box with a revision-cloud border and one arrow
 * leader to `anchor`; `cloudCallout` (Cloud+) is its twin, kept for its own tool.
 */
export type MarkupShape =
    | (MarkupBase & { type: "rectangle"; bounds: Bounds; rotation?: number })
    | (MarkupBase & { type: "ellipse"; bounds: Bounds; rotation?: number })
    | (MarkupBase & { type: "cloud"; bounds: Bounds; rotation?: number })
    | (MarkupBase & { type: "line"; start: Pt; end: Pt })
    | (MarkupBase & { type: "arrow"; start: Pt; end: Pt })
    | (MarkupBase & { type: "freehand"; points: Pt[] })
    // Vertex kinds (polyline open, polygon closed), so no rotation; `fill` fills a polygon.
    | (MarkupBase & { type: "polyline"; points: Pt[] })
    | (MarkupBase & { type: "polygon"; points: Pt[]; fill?: boolean })
    // Measurement labels come from geometry and the calibration, never persisted.
    // `measureLength` is open unless the perimeter preset closes it.
    | (MarkupBase & { type: "measureLength"; points: Pt[]; preset?: "perimeter" })
    | (MarkupBase & { type: "measureArea"; points: Pt[]; holes?: Pt[][] })
    // Angle: vertex, first ray, second ray. Radius and diameter: center, edge.
    | (MarkupBase & { type: "measureAngle"; points: readonly [Pt, Pt, Pt] })
    | (MarkupBase & { type: "measureRadius"; points: readonly [Pt, Pt] })
    | (MarkupBase & { type: "measureDiameter"; points: readonly [Pt, Pt] })
    | (MarkupBase & {
          type: "measureVolume";
          points: Pt[];
          depthPx: number;
          worldDepth: number;
      })
    // One tally marker per point; `symbol` picks the marker (absent, a numbered dot).
    | (MarkupBase & { type: "count"; points: Pt[]; symbol?: string })
    // Fixed screen-size icons at `at`; a flag's `color` is its status color.
    | (MarkupBase & { type: "note"; at: Pt; text: string })
    | (MarkupBase & { type: "flag"; at: Pt; label?: string })
    // The bytes live in a SHA-256-keyed sidecar, never in the shapes JSON.
    | (MarkupBase & { type: "image"; bounds: Bounds; imageRef: string; rotation?: number })
    // An image snapshot: a region of another page instead of sidecar bytes; `imageRef` names
    // the picture this tab took of it, which the server ignores.
    | (MarkupBase & {
          type: "image";
          bounds: Bounds;
          snapshot: MarkupSnapshot;
          imageRef?: string;
          rotation?: number;
      })
    | (MarkupBase & {
          type: "text";
          bounds: Bounds;
          text: string;
          fontSize: number;
          rotation?: number;
      })
    | (MarkupBase & {
          type: "callout";
          bounds: Bounds;
          anchor: Pt;
          text: string;
          fontSize: number;
          rotation?: number;
      })
    | (MarkupBase & {
          type: "cloudCallout";
          bounds: Bounds;
          anchor: Pt;
          text: string;
          fontSize: number;
          rotation?: number;
      })
    // Exactly 2 points, measured like measureLength, drawn with ticks and two arrowheads;
    // built only by `buildDimension`.
    | (MarkupBase & { type: "dimension"; points: readonly [Pt, Pt] })
    // Three stamp content kinds share one `type` (schema 1.3.0): a preset label, custom text
    // with dynamic fields, or an imported image. Exactly one of `preset`/`custom`/`imageRef`
    // is set, and `stampContentKind` alone decides which.
    | (MarkupBase & { type: "stamp"; bounds: Bounds; preset: StampPreset; rotation?: number })
    | (MarkupBase & { type: "stamp"; bounds: Bounds; custom: StampCustomText; rotation?: number })
    | (MarkupBase & {
          type: "stamp";
          bounds: Bounds;
          imageRef: string;
          /** The image's natural width/height ratio (> 0), held by a resize drag. */
          imageAspectRatio: number;
          rotation?: number;
      })
    // Highlight, underline or strikeout over selected document text: one axis-aligned display-
    // space rect per selected line-run (the exporter maps each to one `/QuadPoints` group
    // through the page's rotation), so no `rotation`. `snippet` is the text's first
    // TEXT_MARKUP_SNIPPET_MAX chars, for the list row and search.
    | (MarkupBase & {
          type: "textMarkup";
          style: TextMarkupStyle;
          quads: readonly Bounds[];
          snippet: string;
      });

/**
 * What an image snapshot shows (MARKUP_SCHEMA.md, server 1.3.0): a region of exactly one source,
 * in that source's displayed frame (normalized 0..1, top-left origin, y down, its own rotation and
 * crop applied). `page_id` is a page of the same hosted document (an attachment set's page, as the
 * set shows it); `section_group_id` is a drawing sheet as the drawing viewer shows it, at
 * `revision_id` when given. `bounds` must have an area. Unknown keys survive (rule 3).
 */
export type MarkupSnapshot =
    | { page_id: string; section_group_id?: never; revision_id?: never; bounds: Bounds }
    | { section_group_id: string; page_id?: never; revision_id?: string; bounds: Bounds };

/** What a snapshot is taken of, without its region: a page of the document, or a drawing sheet. */
export type SnapshotSourceRef =
    { page_id: string } | { section_group_id: string; revision_id?: string };

/** An image snapshot placed on a page: where it sits (`bounds`), what it shows (`snapshot`). */
export type SnapshotImageShape = Extract<MarkupShape, { type: "image"; snapshot: MarkupSnapshot }>;

/** Whether `shape` is an image snapshot rather than an image with sidecar bytes. */
export function isSnapshotShape(shape: MarkupShape): shape is SnapshotImageShape {
    return shape.type === "image" && "snapshot" in shape && shape.snapshot !== undefined;
}

/**
 * Where a markup's shapes are saved, as far as which of them the save keeps: a local document's
 * own file (`local`) keeps every kind; the server keeps its vocabulary (`SERVER_SHAPE_TYPES`), plus
 * image snapshots where the surface stores them (`storesSnapshots`).
 */
export interface MarkupSaveSurface {
    local?: boolean;
    storesSnapshots: boolean;
}

/**
 * Whether a save to `surface` keeps shapes of `kind`. The server stores a snapshot only on a layer
 * whose PDF writer reads the page it shows (an attachment set's page), so an image snapshot is
 * judged by the shape (`keepsShape`), never by its kind. With `keepsShape` and `savableShapes`,
 * the one place this is decided: the tools a markup offers, the shapes a paste or a Tool Chest card
 * may put on a page, and the shapes a save sends all read it.
 */
export function keepsKind(kind: string, surface: { local?: boolean }): boolean {
    return surface.local === true || SERVER_SHAPE_TYPES.has(kind);
}

/** Whether a save to `surface` keeps `shape` (see `keepsKind`). */
export function keepsShape(shape: MarkupShape, surface: MarkupSaveSurface): boolean {
    return keepsKind(shape.type, surface) || (surface.storesSnapshots && isSnapshotShape(shape));
}

/**
 * The shapes a save to a surface keeps (`keepsShape`). A surface that refuses a kind refuses the
 * whole page with a 422, so a save to the server sends only these.
 */
export function savableShapes(
    shapes: readonly MarkupShape[],
    surface: MarkupSaveSurface,
): MarkupShape[] {
    return shapes.filter((shape) => keepsShape(shape, surface));
}

/** A stamp shape, in any of its three content kinds. */
export type StampShape = Extract<MarkupShape, { type: "stamp" }>;

/** Which of a stamp's content kinds `shape` carries: the one place every reader asks. */
export function stampContentKind(shape: StampShape): "preset" | "custom" | "image" {
    if ("custom" in shape) return "custom";
    if ("imageRef" in shape) return "image";
    return "preset";
}

/**
 * A drawing scale: `worldPerPx` is real-world units per page pixel (zoom- and page-size-
 * independent, and free of a non-square page's anisotropy); `precision` is decimal places for
 * metric labels (imperial "ft" renders feet-inches).
 */
export interface Calibration {
    worldPerPx: number;
    unit: string;
    precision: number;
}

/** Which markup layers a download carries; both default to included. */
export interface MarkupDownloadOptions {
    /** Other users' published sets (the dimmed read-only group). */
    includePublished: boolean;
    /** The caller's own draft shapes (the foreground editable group). */
    includeUnpublished: boolean;
}

/** What a download covers: the whole open file, or only the viewed spec section's pages
 *  cut from the book that holds it. */
export type MarkupDownloadTarget = "file" | "section";
